import React, { Suspense } from "react";
import {
	BrowserRouter as Router,
	Routes,
	Route,
	Navigate,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";

import { useEffect } from "react";
import { useAuthStore } from "@/pages/Landing/hooks/useAuth/use-auth.store";

// Lazy load pages
const LandingPage = React.lazy(() => import("@/pages/Landing"));
const DashboardPage = React.lazy(() => import("@/pages/dashboard"));
const PrivacyPage = React.lazy(() => import("@/pages/Privacy"));
const SubscriptionCallbackPage = React.lazy(
	() => import("@/pages/SubscriptionCallback")
);
const AdminPage = React.lazy(() =>
	import("@/features/admin").then((mod) => ({ default: mod.AdminPage }))
);

// Protected Route Component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
	const { isAuthenticated, isLoading } = useAuthStore();

	if (isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-900">
				<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
			</div>
		);
	}

	if (!isAuthenticated) {
		return <Navigate to="/landing" replace />;
	}

	return <>{children}</>;
};

// Admin Protected Route - Only for admin users
const AdminProtectedRoute = ({ children }: { children: React.ReactNode }) => {
	const { isAuthenticated, isLoading, user } = useAuthStore();

	// Debug: verificar role do usuário
	console.log('[AdminProtectedRoute] user:', user);
	console.log('[AdminProtectedRoute] role:', user?.role);

	if (isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-900">
				<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
			</div>
		);
	}

	if (!isAuthenticated) {
		return <Navigate to="/landing" replace />;
	}

	if (user?.role !== "admin") {
		console.log('[AdminProtectedRoute] Redirecting to dashboard - role is not admin:', user?.role);
		return <Navigate to="/dashboard" replace />;
	}

	return <>{children}</>;
};

// Root Route - Conditional redirect
const RootRoute = () => {
	const { isAuthenticated, isLoading } = useAuthStore();

	if (isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-900">
				<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
			</div>
		);
	}

	return <Navigate to={isAuthenticated ? "/dashboard" : "/landing"} replace />;
};

function App() {
	const initializeAuth = useAuthStore((state) => state.initializeAuth);

	useEffect(() => {
		initializeAuth();
	}, [initializeAuth]);

	return (
		<HelmetProvider>
			<Router>
				<Suspense
					fallback={
						<div className="min-h-screen flex items-center justify-center bg-gray-900">
							<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
						</div>
					}
				>
					<Routes>
						<Route path="/landing" element={<LandingPage />} />
					<Route path="/" element={<RootRoute />} />
						<Route path="/privacy" element={<PrivacyPage />} />
						<Route
							path="/subscription/callback"
							element={<SubscriptionCallbackPage />}
						/>
						<Route
							path="/dashboard"
							element={
								<ProtectedRoute>
									<DashboardPage />
								</ProtectedRoute>
							}
						/>
						<Route
							path="/admin"
							element={
								<AdminProtectedRoute>
									<AdminPage />
								</AdminProtectedRoute>
							}
						/>
					</Routes>
				</Suspense>
				<Toaster position="top-right" theme="dark" richColors />
			</Router>
		</HelmetProvider>
	);
}

export default App;
